/**
 * Wraps a control with label, optional hint, and optional inline error text.
 *
 * @param options - Stable **`id`** for **`label[for]`**, visible label, control element, hint, and error copy.
 * @returns Field container with **`qz-field`** class.
 */
export function createField(
  options: Readonly<{
    id: string;
    label: string;
    control: HTMLElement;
    hint?: string;
    errorText?: string;
  }>,
): HTMLDivElement {
  const root = document.createElement("div");
  root.className = "qz-field";

  const lbl = document.createElement("label");
  lbl.className = "qz-field__label";
  lbl.htmlFor = options.id;
  lbl.textContent = options.label;
  options.control.id = options.id;
  root.appendChild(lbl);

  let describedBy = "";
  if (options.hint !== undefined && options.hint.length > 0) {
    const hint = document.createElement("p");
    hint.className = "qz-field__hint";
    hint.id = `${options.id}-hint`;
    hint.textContent = options.hint;
    root.appendChild(hint);
    describedBy = hint.id;
  }

  root.appendChild(options.control);

  if (describedBy.length > 0) {
    const existing = options.control.getAttribute("aria-describedby");
    options.control.setAttribute(
      "aria-describedby",
      existing ? `${existing} ${describedBy}` : describedBy,
    );
  }

  if (options.errorText !== undefined && options.errorText.length > 0) {
    const err = document.createElement("p");
    err.className = "qz-field__error";
    err.id = `${options.id}-error`;
    err.setAttribute("role", "alert");
    err.textContent = options.errorText;
    root.appendChild(err);
    options.control.setAttribute("aria-invalid", "true");
    const existing = options.control.getAttribute("aria-describedby");
    options.control.setAttribute(
      "aria-describedby",
      existing ? `${existing} ${err.id}` : err.id,
    );
  }

  return root;
}
